import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ClickableOverlay, PropMetadata } from "../../dto/PropMetadata";
import { RootState } from "../Store";
import { TranslatedPropCategoryDTO } from "../../dto/TranslatedPropCategoryDTO";
import { TranslatedPropDTO } from "../../dto/TranslatedPropDTO";
import { PropInteractionDTO } from "../../dto/PropInteractionDTO";
import { TranslatedPropContentDTO } from "../../dto/TranslatedPropContentDTO";
import { UsedProp } from "../../models/UsedProp";

export interface PropLibraryState {
  isInitialized: boolean;
  selectedPropId: string;
  categories: TranslatedPropCategoryDTO[];
  interactions: PropInteractionDTO[];
  filter: string;
  filteredCategory: string;
  pagedProps: TranslatedPropDTO[];
  selectedPropContent: TranslatedPropContentDTO | null;
  selectedPropMetadata: PropMetadata | null;
  usedProps: Record<string, UsedProp>; // [propId: string]: UsedProp;
  cachedPropRenderThumbnails: Record<string, string>;
}

const initialPropLibraryState: PropLibraryState = {
  isInitialized: false,
  selectedPropId: "",
  categories: [],
  interactions: [],
  filter: "",
  filteredCategory: "",
  pagedProps: [],
  selectedPropContent: null,
  selectedPropMetadata: null,
  usedProps: {},
  cachedPropRenderThumbnails: {},
};

export const getPropRenderThumbnailCacheKey = (
  propType: 0 | 1,
  propId: string,
  renderId: string
) => `${propType}:${propId}:${renderId}`;

const EMPTY_CLICKABLE_OVERLAYS: ClickableOverlay[] = [];

export const getClickableOverlaysForInspectDefinition = (
  metadata: PropMetadata | null | undefined,
  inspectDefinitionId?: string
): ClickableOverlay[] => {
  if (!inspectDefinitionId) return EMPTY_CLICKABLE_OVERLAYS;
  return (
    metadata?.clickableOverlaysByInspectId?.[inspectDefinitionId] ??
    EMPTY_CLICKABLE_OVERLAYS
  );
};

export const propLibrarySlice = createSlice({
  name: "propLibrary",
  initialState: initialPropLibraryState,
  reducers: {
    initializePropLibrary: (
      state,
      action: PayloadAction<{
        totalCount: number;
        firstPage: TranslatedPropDTO[];
      }>,
    ) => {
      const { totalCount, firstPage } = action.payload;
      state.isInitialized = true;
      state.pagedProps = new Array(totalCount).fill({
        id: "unloaded",
        name: "unloaded",
        downloadUri: "",
        categories: [],
        version: 0,
      } as TranslatedPropDTO);

      for (let i = 0; i < firstPage.length; i++) {
        state.pagedProps[i] = firstPage[i];
      }
    },
    addPagedProps: (
      state,
      action: PayloadAction<{
        pageNumber: number;
        assets: TranslatedPropDTO[];
      }>,
    ) => {
      var si = action.payload.pageNumber * 10;
      if (si > state.pagedProps.length) return;

      for (var i = 0; i < action.payload.assets.length; i++) {
        state.pagedProps[si + i] = action.payload.assets[i];
      }
    },
    setSelectedProp: (state, action: PayloadAction<string>) => {
      state.selectedPropId = action.payload;
    },
    setPropCategories: (
      state,
      action: PayloadAction<TranslatedPropCategoryDTO[]>,
    ) => {
      state.categories = action.payload;
    },
    setPropInteractions: (
      state,
      action: PayloadAction<PropInteractionDTO[]>,
    ) => {
      state.interactions = action.payload;
    },
    setPropFilter: (state, action: PayloadAction<string>) => {
      state.filter = action.payload;
    },
    setPropCategoryFilter: (state, action: PayloadAction<string>) => {
      state.filteredCategory = action.payload;
    },
    setSelectedPropContent: (
      state,
      action: PayloadAction<{
        content: TranslatedPropContentDTO;
        metadata: PropMetadata;
      }>,
    ) => {
      state.selectedPropContent = action.payload.content;
      state.selectedPropMetadata = action.payload.metadata;
    },
    addSelectedPropAsUsedProp: (state) => {
      if (!state.selectedPropContent || !state.selectedPropMetadata) return;

      state.usedProps[state.selectedPropContent.id] = {
        propContent: state.selectedPropContent,
        metadata: state.selectedPropMetadata,
      };
    },
    addSituationAsUsedProp: (
      state,
      action: PayloadAction<{
        id: string;
        propContent: TranslatedPropContentDTO;
        metadata: PropMetadata;
      }>,
    ) => {
      state.usedProps[action.payload.id] = {
        propContent: action.payload.propContent,
        metadata: action.payload.metadata,
      };
    },
    addUsedProps: (state, action: PayloadAction<UsedProp[]>) => {
      for (let i = 0; i < action.payload.length; i++) {
        state.usedProps[action.payload[i].propContent.id] = action.payload[i];
      }
    },
    updateUsedProp: (state, action: PayloadAction<PropMetadata>) => {
      const usedProp = state.usedProps[action.payload.id];
      if (!usedProp) {
        console.log("Used prop not found " + action.payload.id);
        return;
      }
      usedProp.metadata = { ...action.payload };
    },
    setCachedPropRenderThumbnail: (
      state,
      action: PayloadAction<{
        propType: 0 | 1;
        propId: string;
        renderId: string;
        downloadUri: string;
      }>,
    ) => {
      const { propType, propId, renderId, downloadUri } = action.payload;
      state.cachedPropRenderThumbnails[
        getPropRenderThumbnailCacheKey(propType, propId, renderId)
      ] = downloadUri;
    },
  },
});

export const {
  initializePropLibrary,
  addPagedProps,
  setSelectedProp,
  setPropCategories,
  setPropInteractions,
  setPropFilter,
  setPropCategoryFilter,
  setSelectedPropContent,
  addSelectedPropAsUsedProp,
  addUsedProps,
  addSituationAsUsedProp,
  updateUsedProp,
  setCachedPropRenderThumbnail,
} = propLibrarySlice.actions;

export const selectIsPropSliceInitialized = (state: RootState) =>
  state.propLibrary.isInitialized;
export const selectPropCategories = (state: RootState) =>
  state.propLibrary.categories;
export const selectPropInteractions = (state: RootState) =>
  state.propLibrary.interactions;
export const selectPropFilter = (state: RootState) => state.propLibrary.filter;
export const selectFilteredCategory = (state: RootState) =>
  state.propLibrary.filteredCategory;
export const selectSelectedPropContent = (state: RootState) =>
  state.propLibrary.selectedPropContent;
export const selectProps = (state: RootState) => state.propLibrary.pagedProps;
export const selectUsedProps = (state: RootState) =>
  state.propLibrary.usedProps;
export const selectCachedPropRenderThumbnails = (state: RootState) =>
  state.propLibrary.cachedPropRenderThumbnails;
export const selectCachedPropRenderThumbnail = (
  state: RootState,
  propType: 0 | 1,
  propId: string,
  renderId: string
) =>
  state.propLibrary.cachedPropRenderThumbnails[
    getPropRenderThumbnailCacheKey(propType, propId, renderId)
  ];

export const selectSelectedProp = (state: RootState) =>
  state.propLibrary.pagedProps.find((prop, index) => {
    return prop.id === state.propLibrary.selectedPropId;
  });
export const selectSelectedPropMetadata = (state: RootState) =>
  state.propLibrary.selectedPropMetadata;
export const selectSelectedPropClickableOverlays = (
  state: RootState,
  inspectDefinitionId?: string
) =>
  getClickableOverlaysForInspectDefinition(
    state.propLibrary.selectedPropMetadata,
    inspectDefinitionId
  );
export const selectSelectedPropId = (state: RootState) =>
  state.propLibrary.selectedPropId;
export const selectPropById = (state: RootState, propId: string) => {
  //return state.propLibrary.props.find(prop => prop.id === propId);
  return state.propLibrary.pagedProps.find((prop) => prop.id === propId);
};

export default propLibrarySlice.reducer;
